import Phaser from 'phaser';
import { audioManager } from '../../core/CoreManagerAudio';
import { Entity, EntityConfig } from '../base/Entity';
import { AbductableBehavior, AbductableConfig } from '../behaviors/AbductableBehavior';
import { Allegiance } from '../../constants/enums';
import { EffectsSystem } from '../../systems/EffectsSystem';

export interface BombConfig extends EntityConfig {
  /** Damage dealt to player on explosion (default: 1) */
  damage?: number;
  /** Fuse time in ms - if not using animation (default: uses animation) */
  fuseTime?: number;
  /** Allegiance - enemy bombs damage player (default: Enemy) */
  allegiance?: Allegiance;
  /** Abductable behavior config */
  behaviors?: {
    abductable?: AbductableConfig;
    [key: string]: unknown;
  };
}

/**
 * Bomb - Timed explosive hazard
 *
 * From C3: bomb (29x29px)
 * - Plays "Bomb" animation as fuse timer (17 frames)
 * - When animation ends → explodes
 * - If player touches (while allegiance=enemy) → explodes + damages player
 * - Can be abducted by tractor beam to neutralize
 *
 * Behavior:
 * - Spawned by destroyed houses
 * - Has AbductableBehavior (can be picked up)
 * - Explodes via timer OR player contact
 */
export class Bomb extends Entity {
  public damage: number;
  public allegiance: Allegiance;
  public abductable: AbductableBehavior;

  private hasExploded: boolean = false;
  private fuseTimer?: Phaser.Time.TimerEvent;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    texture: string = 'bomb',
    config: BombConfig = {}
  ) {
    super(scene, x, y, texture, config);

    this.damage = config.damage ?? 1;
    this.allegiance = config.allegiance ?? Allegiance.Enemy;

    // Bombs have no gravity initially (fall via AbductableBehavior)
    if (this.body) {
      const body = this.body as Phaser.Physics.Arcade.Body;
      body.setAllowGravity(false);
    }

    // Add abductable behavior - bombs can be picked up by tractor beam
    this.abductable = this.addBehavior(new AbductableBehavior(this, {
      bioMatter: 0, // No biomatter reward
      weight: 'light',
      canAbductWhileDying: false,
      ...config.behaviors?.abductable
    }));

    // Start the fuse animation/timer
    this.startFuse(config.fuseTime);
  }

  /**
   * Start the bomb fuse (animation or timer)
   */
  private startFuse(fuseTimeMs?: number): void {
    const animKey = `${this.texture.key}_Bomb`;

    // Try to play the Bomb animation
    if (this.scene.anims.exists(animKey)) {
      this.play(animKey);
      this.once('animationcomplete', () => {
        this.explode();
      });
    } else {
      // Fallback: use timer if no animation
      const fuseTime = fuseTimeMs ?? 3000; // Default 3 second fuse
      this.fuseTimer = this.scene.time.delayedCall(fuseTime, () => {
        this.explode();
      });
    }
  }

  /**
   * Explode the bomb
   * Note: Player damage is handled by CollisionSystem, not here
   */
  explode(): void {
    if (this.hasExploded) return;
    this.hasExploded = true;

    // Cancel any pending timer
    if (this.fuseTimer) {
      this.fuseTimer.destroy();
      this.fuseTimer = undefined;
    }

    // Play explosion sound (respeta volumen global)
    audioManager.playSfx('sfx_explosion_small', 'explosion', this.scene);

    // Spawn explosion effect
    this.spawnExplosionEffect();

    // Camera shake
    this.scene.cameras.main?.shake(80, 0.008);

    // Emit bomb exploded event (for scoring, etc.)
    this.scene.events.emit('bombExploded', {
      bomb: this,
      x: this.x,
      y: this.y
    });

    // Destroy the bomb
    this.destroy();
  }

  /**
   * Spawn the explosion visual effect
   */
  private spawnExplosionEffect(): void {
    const effectsSystem = this.scene.registry.get('effectsSystem') as EffectsSystem;

    if (effectsSystem) {
      effectsSystem.spawn(
        {
          sprite: 'playerexplotion',
          animation: 'playerexplotion_Default',
          fadeOut: 0.3,
          scale: 1
        },
        this.x,
        this.y
      );
    } else {
      // Fallback: create simple explosion sprite
      const explosion = this.scene.add.sprite(this.x, this.y, 'playerexplotion');
      const explosionAnimKey = 'playerexplotion_Default';
      if (this.scene.anims.exists(explosionAnimKey)) {
        explosion.play(explosionAnimKey);
        explosion.once('animationcomplete', () => {
          explosion.destroy();
        });
      } else {
        // No animation, just fade out
        this.scene.tweens.add({
          targets: explosion,
          alpha: 0,
          duration: 300,
          onComplete: () => explosion.destroy()
        });
      }
    }
  }

  /**
   * Check if bomb is still active (not exploded)
   */
  isActive(): boolean {
    return !this.hasExploded && this.active;
  }

  /**
   * Clean up on destroy
   */
  destroy(fromScene?: boolean): void {
    if (this.fuseTimer) {
      this.fuseTimer.destroy();
      this.fuseTimer = undefined;
    }
    super.destroy(fromScene);
  }
}
